import { Calendar, CircleAlert, DollarSign, FilePlus, Folder, IdCard, ListFilter, MapPin, Pencil, Settings, ShoppingCart, Users } from 'lucide-react';
import * as moment from 'moment';
import { ITag } from 'proto/protobufs';
import * as React from 'react';
import { getTranslate } from 'react-localize-redux';
import { useRecoilValue } from 'recoil';
import { canUserAccess } from '../../../atoms/allowDenyList';
import { AFormTemplate } from '../../../atoms/forms';
import { ARefresh } from '../../../atoms/global/refresh';
import Avatar from '../../../components_v2/avatar/Avatar';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import { getTags } from '../../../components_v2/toolbarFilter/tags/action';
import { TagType } from '../../../components_v2/toolbarFilter/tags/Model';
import storeLang from '../../../helpers/storeLang';
import { Loader } from '../../../styles/global/css/GlobalLoader';
import { Open } from '../../../styles/global/css/Open';
import {
	AmberSidely,
	PrimaryColor,
	BrightYellowSidely,
	CoralSidely,
	DarkGreySidely,
	EmeraldSidely,
	GreenSidely,
	LimeYellowSidely,
	RedSidely
} from '../../../styles/global/css/Utils';
import { UserToolTip } from '../../../styles/global/ToolTip';
import { Translate, translateToString } from '../../../styles/global/translate';
import PopupEvent from '../../calendar/popup/Event';
import ContactPopup from '../../contact/popup/ContactPopup';
import OpportunityPopup from '../../opportunities/popup/OpportunityPopup';
import { LoadingStateEnum } from '../../import/model';
import NoData from '../../noData/NoData';
import PopupNote from '../../notes/popup/PopupNote';
import { Owner } from '../../orders/model/Model';
import { ModalState } from '../../products/model';
import { FlexDiv } from '../../products/style';
import { getCompanyTimeline } from '../data/action';
import { CompanyTimeline, TimelineElement } from '../model/Model';
import getScope from '../../permissions/getScope';
import { Scope } from '../../../web-types/objectActions';
import {
	BodyBlock,
	BodyMainImage,
	BodyMainLoaderContainer,
	BodyMainText,
	ContainerPoint
} from '../style/PopupStyle';
import {
	Span,
	TimelineBodyMainContainer,
	TimelineCollapse,
	TimelineContainer,
	TimelineDate,
	TimelineGroup,
	TimelineHeader,
	TimelineHeaderLine
} from '../style/TimelineStyle';
import { updateEvent } from './action';


function Timeline(props: {
	clientCompanyId: number,
	isExtend: boolean,
	onRefresh: (element?: TimelineElement, owner?: Owner, newSearch?: string) => void,
	onTimelineScroll: (newValues: CompanyTimeline[]) => void,
	onNoteDelete: (id: number) => void,
	limit: number,
	timeline: CompanyTimeline[],
	owners: Owner[],
	onDisableClickOut: (b: boolean) => void
	toggleShelfAuditPopup: (id: number, full) => void,
	toggleFormPopup: (uuid: string) => void
}): JSX.Element {
	const translate = getTranslate(storeLang.getState().localize);

	const [offset, setOffset] = React.useState<number>(0);
	const [timelineFinished, setTimelineFinished] = React.useState<boolean>(false);

	const [loading, setLoading] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const [bottomLoading, setBottomLoading] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);

	const [selectedOwner, setSelectedOwner] = React.useState<Owner>();

	const [search] = React.useState<string>();
	const [selectedElement, setSelectedElement] = React.useState<TimelineElement>();

	// The timeline gathers what the other sections of the page show, so it
	// answers to the same permissions: a reader with no calendar saw the visits
	// here that the right-hand column was hiding from them. The server decides
	// what comes back; these two decide what is worth offering to filter on.
	const kinds: { element: TimelineElement, scope: Scope | undefined }[] = [
		{ element: TimelineElement.CONTACT, scope: getScope('ReadContact') },
		{ element: TimelineElement.EVENT, scope: getScope('ReadEvent') },
		{ element: TimelineElement.NOTE, scope: getScope('ReadCompanyNote') },
		{ element: TimelineElement.STATUS_CHANGE, scope: getScope('ReadCompany') },
		{ element: TimelineElement.FORM, scope: getScope('ReadForm') },
		{ element: TimelineElement.CREATED_DOCUMENT, scope: getScope('ReadCompanyDocument') },
	];
	const readable = kinds.filter(kind => kind.scope !== 'Nothing');
	// nobody else's entries can come back, so there is nobody else to pick
	const onlyMine = readable.length > 0 && readable.every(kind => kind.scope === 'User');
	const pickableOwners = onlyMine ? props.owners.filter(owner => owner.isYou) : props.owners;
	const [isNoteOpen, setIsNoteOpen] = React.useState<[boolean, number | undefined]>([false, undefined]);
	const [isEventOpen, setIsEventOpen] = React.useState<[boolean, number | undefined]>([false, undefined]);
	const [isContactOpen, setIsContactOpen] = React.useState<ModalState<number>>({ isOpen: false });
	const [isOpportunityOpen, setIsOpportunityOpen] = React.useState<ModalState<number>>({ isOpen: false });
	const [tags, setTags] = React.useState<ITag[]>([]);
	const [monthOpen, setMonthOpen] = React.useState<{ id: string, value: boolean }[]>([]);
	const canUserAccessF = canUserAccess();
	const formTemplates = useRecoilValue(AFormTemplate).instanceForms;
	const formNameIdMap = new Map(formTemplates.map(t => [t.name, t.id]));

	const refreshingAtom = useRecoilValue(ARefresh);

	// any full refresh restarts pagination: keeping the old offset made the
	// next scroll fetch page N of the new result set, silently skipping rows
	const resetPagination = () => {
		setOffset(0);
		setTimelineFinished(false);
	};

	React.useEffect(() => {
		if (refreshingAtom.timeline !== undefined) {
			resetPagination();
			props.onRefresh(selectedElement, selectedOwner, search);
		}
	}, [refreshingAtom.timeline]);

	// The grouping works in LOCAL time (created_at is UTC): grouping on the
	// raw string while the display converts to local made items around
	// midnight or a month boundary show a date outside their own group.
	const { upcoming, months } = React.useMemo(() => {
		const now = moment();
		const sorted = [...props.timeline]
			.filter((t) => t.action_type !== 'FORM' || !t.title || canUserAccessF('ReadForm', formNameIdMap.get(t.title)))
			.sort((a, b) => moment.utc(b.created_at).valueOf() - moment.utc(a.created_at).valueOf());
		const upcoming: CompanyTimeline[] = [];
		const months: { key: string, month: number, year: number, items: CompanyTimeline[] }[] = [];
		for (const t of sorted) {
			const local = moment.utc(t.created_at).local();
			// strictly future goes to upcoming; today's past hours stay in the
			// month list — they used to render in BOTH sections
			if (local.isAfter(now)) {
				upcoming.push(t);
				continue;
			}
			const key = `${local.year()}_${local.month()}`;
			const last = months[months.length - 1];
			if (last && last.key === key) last.items.push(t);
			else months.push({ key, month: local.month(), year: local.year(), items: [t] });
		}
		upcoming.reverse(); // soonest first
		return { upcoming, months };
	}, [props.timeline]);

	React.useEffect(() => {
		setLoading(LoadingStateEnum.LOADED);
		const existingIds = new Set(monthOpen.map(m => m.id));
		const newMonthOpen = existingIds.has('upcoming')
			? []
			: [{ id: 'upcoming', value: true }];

		months.forEach(m => {
			if (!existingIds.has(m.key)) {
				newMonthOpen.push({ id: m.key, value: true });
			}
		});

		if (newMonthOpen.length > 0) {
			setMonthOpen(prev => [...prev, ...newMonthOpen]);
		}
	}, [months]);


	React.useEffect(() => {
		getTags(TagType.CONTACT).then(setTags);
	}, []);

	const hasFilters = selectedOwner != null || selectedElement != null;

	function applyFilters(element: TimelineElement | undefined, owner: Owner | undefined): void {
		setSelectedElement(element);
		setSelectedOwner(owner);
		setLoading(LoadingStateEnum.LOADING);
		resetPagination();
		props.onRefresh(element, owner, search);
	}

	const ownerOptions = React.useMemo<DropdownData<Owner>[]>(() => pickableOwners.map(o => ({
		label: o.name,
		value: o,
		image: o.photoUrl,
		imageJsx: o.photoUrl ? undefined : <Avatar backgroundColor={o.color} width={'26px'} name={o.name} />
	})), [props.owners, onlyMine]);

	const elementOptions = React.useMemo<DropdownData<TimelineElement>[]>(() => Object.values(TimelineElement)
		.filter(value => readable.some(kind => kind.element === value))
		.sort((a, b) => a.localeCompare(b))
		.map(value => ({
			label: translate(`company.detail.timeline.${value.replace(/(.)([A-Z])/g, '$1_$2').toLowerCase()}`).toString(), // ShelfAudit => shelf_audit, StatusChange => status_change
			value
		})), []);


	function getActionTypeProperty(timeline: CompanyTimeline): { color: string, img: JSX.Element, msg: string, url?: string, noname: string, onClick?: () => void, hideName?: boolean, title?: string } {
		switch (timeline.action_type) {
			case 'OPPORTUNITY':
				return { color: AmberSidely, img: <DollarSign size={26} color='white' />, msg: translate('company.detail.timeline.opportunity_created').toString(), onClick: () => setIsOpportunityOpen({ isOpen: true, data: timeline.id }), noname: translate('company.detail.timeline.noname').toString() };
			case 'CONTACT':
				return { color: GreenSidely, img: <IdCard size={26} color='white' />, msg: translate('company.detail.timeline.contact_created').toString(), onClick: () => setIsContactOpen({ isOpen: true, data: timeline.id }), noname: translate('company.detail.timeline.noname').toString() };
			case 'EVENT':
				return { color: PrimaryColor, img: <Calendar size={26} color='white' />, msg: `${translate('company.detail.timeline.event').toString()}${timeline.sub_title && timeline.sub_title.length > 0 ? ` (${translate(`company.detail.timeline.${timeline.sub_title.toLowerCase()}`).toString()}) ` : ''}`, onClick: () => { setIsEventOpen([true, timeline.id]); props.onDisableClickOut(true); }, noname: translate('company.detail.timeline.noname').toString() };
			case 'ORDER':
				return { color: LimeYellowSidely, img: <ShoppingCart size={26} color='white' />, msg: translate('company.detail.timeline.order_created').toString(), url: `/orders?id=${timeline.id}`, noname: translate('company.detail.timeline.noname').toString() };
			case 'SHELF_AUDIT':
				return { color: RedSidely, img: <Users size={26} color='white' />, msg: translate('company.detail.timeline.shelf_audit_created').toString(), onClick: () => props.toggleShelfAuditPopup(timeline.id!, false), noname: timeline.sub_title ?? translate('company.detail.timeline.noname').toString() };
			case 'NOTE':
				return { color: BrightYellowSidely, img: <Pencil size={26} color='white' />, msg: translate('company.detail.timeline.note_created').toString(), onClick: () => setIsNoteOpen([true, timeline.id]), noname: translate('company.detail.timeline.noname').toString() };
			case 'STATUS_CHANGE':
				return { color: EmeraldSidely, img: <Settings size={26} color='white' />, msg: translate('company.detail.timeline.status_changed').toString(), noname: translate('company.detail.timeline.noname').toString() };
			case 'CREATED_DOCUMENT':
				return { color: CoralSidely, img: <Folder size={26} color='white' />, msg: translate('company.detail.timeline.document_created').toString(), noname: translate('company.detail.timeline.noname').toString(), url: timeline.sub_title };
			case 'CHECK_IN':
				return { color: AmberSidely, img: <MapPin size={26} color='white' />, msg: translate('company.detail.timeline.check_in_created').toString(), noname: translate('company.detail.timeline.noname').toString(), hideName: true };
			case 'FORM':
				return { color: RedSidely, img: <FilePlus size={26} color='white' />, msg: translate('company.detail.timeline.form_created').toString(), noname: translate('company.detail.timeline.noname').toString(), onClick: () => props.toggleFormPopup(timeline.uuid!), title: timeline.sub_title ? `(${translateToString('draft')}) ${timeline.title}` : timeline.title };
			default:
				return { color: DarkGreySidely, img: <CircleAlert strokeWidth={2.5} color={AmberSidely} size={26} />, msg: translate('company.detail.timeline.not_defined').toString(), noname: translate('company.detail.timeline.noname').toString() };
		}
	}

	function getMonthString(month: number): string {
		switch (month) {
			case 0:
				return translate('date.january').toString();
			case 1:
				return translate('date.february').toString();
			case 2:
				return translate('date.march').toString();
			case 3:
				return translate('date.april').toString();
			case 4:
				return translate('date.may').toString();
			case 5:
				return translate('date.june').toString();
			case 6:
				return translate('date.july').toString();
			case 7:
				return translate('date.august').toString();
			case 8:
				return translate('date.september').toString();
			case 9:
				return translate('date.october').toString();
			case 10:
				return translate('date.november').toString();
			case 11:
				return translate('date.december').toString();
			default:
				return '';
		}
	}

	async function endScroll(e: HTMLDivElement): Promise<void> {
		if (timelineFinished || bottomLoading === LoadingStateEnum.LOADING) return;
		if (e.offsetHeight + e.scrollTop >= e.scrollHeight - 200) {
			const newOffset = offset + props.limit;
			setOffset(newOffset);
			setBottomLoading(LoadingStateEnum.LOADING);
			const res = await getCompanyTimeline({
				client_company_id: props.clientCompanyId,
				limit: props.limit,
				offset: newOffset,
				element: selectedElement,
				search,
				user_id: selectedOwner?.id
			});
			if (res.length == 0 || res.length < props.limit) setTimelineFinished(true);
			if (res.length !== 0) props.onTimelineScroll(res);
			setBottomLoading(LoadingStateEnum.LOADED);
		}
	}

	const renderItem = (t2: CompanyTimeline) => {
		const property2 = getActionTypeProperty(t2);
		return (
			<>
				<BodyBlock flexDirection={'row'} isOpen={true} alignItems='center'>
					<ContainerPoint color={property2.color}>{property2.img}</ContainerPoint>
					<BodyBlock isOpen={true} marginLeft="15px" flexDirection="column">
						<BodyMainText marginLeft="0px">{property2.msg}
							{
								property2.hideName ?
									<></>
									:
									<Span noClick={!property2.onClick && !property2.url} onClick={() => (property2.onClick?.()) ?? (property2.url && window.open(property2.url, '_blank'))}>{property2.title ?? (t2.title && t2.title.length > 0 ? t2.title : property2.noname)}</Span>
							}
						</BodyMainText>
						<BodyBlock isOpen={true} flexDirection={'row'}>
							<div style={{ margin: '0 8px 0 0' }}>
								<UserToolTip userId={t2.user_id} left='-34px'>
									{t2.user_photo ? <BodyMainImage src={t2.user_photo} /> : <Avatar width='20px' userId={t2.user_id} name={t2.user_name ?? ''} fontSize='9px' />}
								</UserToolTip>
							</div>
							<BodyMainText marginLeft="0px">{moment.utc(t2.created_at).local().format(`DD/MM/YYYY [${translate('global.at').toString()}] HH:mm`)}</BodyMainText>
						</BodyBlock>
					</BodyBlock>
				</BodyBlock>
			</>
		);
	};

	return (
		<TimelineContainer>
			<TimelineHeader isOpen={props.isExtend}>
				<TimelineHeaderLine>
					<Dropdown
						label={<FlexDiv gap='4px'><Users size={14} /><Translate id='filter_by_user' /></FlexDiv>}
						placeholder={translate('all_users').toString()}
						dropdownStyle={{
							width: '200px',
							optionWidth: '200px',
							labelUp: true,
							optionHeight: '200px',
							labelStyle: { padding: '0 0 4px 0' }
						}}
						readOnly
						cancellable
						name="user_filter"
						datalist={ownerOptions}
						selectedValue={selectedOwner ? ownerOptions.find(o => o.value.id === selectedOwner.id) : undefined}
						onChange={(value: DropdownData<Owner | null>) => applyFilters(selectedElement, value.value ?? undefined)}
					/>
					<Dropdown
						label={<FlexDiv gap='4px'><ListFilter size={14} /><Translate id='filter_by_type' /></FlexDiv>}
						searchable={false}
						placeholder={translate('all_types').toString()}
						dropdownStyle={{
							width: '200px',
							optionWidth: '200px',
							labelUp: true,
							optionHeight: '200px',
							labelStyle: { padding: '0 0 4px 0' }
						}}
						readOnly
						cancellable
						name="element_filter"
						datalist={elementOptions}
						selectedValue={selectedElement ? elementOptions.find(o => o.value === selectedElement) : undefined}
						onChange={(value: DropdownData<TimelineElement | null>) => applyFilters(value.value ?? undefined, selectedOwner)}
					/>
				</TimelineHeaderLine>
			</TimelineHeader>
			<TimelineBodyMainContainer onScroll={(e) => endScroll(e.currentTarget)}>
				{
					loading === LoadingStateEnum.LOADING &&
					<Loader center width="30px" />
				}
				{
					loading === LoadingStateEnum.LOADED && props.timeline.length <= 0 && (hasFilters
						? <NoData
							isNoResult
							message={translateToString('No results found')}
							messageButton={<Translate id='reset_filters' />}
							onClickButton={() => applyFilters(undefined, undefined)}
						/>
						: <NoData message={translate('global.nodata.message.company').toString()} messageButton='' urlButton='' />)
				}
				{upcoming.length > 0 &&
					<TimelineGroup>
						<FlexDiv gap='8px' clickable onClick={() => setMonthOpen(monthOpen.map(m => {
							if (m.id === 'upcoming') {
								m.value = !m.value;
							}
							return m;
						}))}>
							<TimelineDate>{translateToString('company.detail.timeline.upcoming')}</TimelineDate>
							<Open isOpen={monthOpen.find(m => m.id === 'upcoming')?.value ?? true} size={11} />
						</FlexDiv>
						<TimelineCollapse isOpen={monthOpen.find(m => m.id === 'upcoming')?.value ?? true}>
							{loading === LoadingStateEnum.LOADED && upcoming.map(t2 => <React.Fragment key={`upcoming[${t2.action_type}][${t2.id ?? t2.uuid}]`}>{renderItem(t2)}</React.Fragment>)}
						</TimelineCollapse>
					</TimelineGroup>
				}
				{
					loading === LoadingStateEnum.LOADED && months.map(group => {
						const isOpen = monthOpen.find(m => m.id === group.key)?.value ?? true;
						return <TimelineGroup key={group.key}>
							<FlexDiv gap='8px' clickable onClick={() => setMonthOpen(monthOpen.map(m => {
								if (m.id === group.key) {
									m.value = !m.value;
								}
								return m;
							}))}>
								<TimelineDate>{`${getMonthString(group.month)} ${group.year}`}</TimelineDate>
								<Open isOpen={isOpen} size={11} />
							</FlexDiv>
							<TimelineCollapse isOpen={isOpen}>
								{group.items.map(t2 => <React.Fragment key={`${group.key}[${t2.action_type}][${t2.id ?? t2.uuid}]`}>{renderItem(t2)}</React.Fragment>)}
							</TimelineCollapse>
						</TimelineGroup>;
					})
				}
			</TimelineBodyMainContainer>
			{
				bottomLoading === LoadingStateEnum.LOADING &&
				<BodyMainLoaderContainer>
					<Loader center width="30px" />
				</BodyMainLoaderContainer>
			}
			<PopupNote
				isOpen={isNoteOpen[0]}
				onClickOut={() => setIsNoteOpen([false, undefined])}
				noteId={isNoteOpen[1]}
				onDelete={(id) => props.onNoteDelete(id)}
			/>
			<PopupEvent
				eventId={isEventOpen[1]}
				isOpen={isEventOpen[0]}
				setIsOpen={(b: boolean) => {
					setIsEventOpen([b, undefined]);
					props.onDisableClickOut(b);
				}}
				onValueChange={(values, eventId) => {
					if (!eventId) return;
					const body = {};
					values.forEach(([key, value]) => body[key] = value);
					updateEvent(eventId, body);
				}}
			/>
			<ContactPopup tags={tags} isOpen={isContactOpen.isOpen} setOpen={isOpen => setIsContactOpen({ isOpen })} contactId={isContactOpen.data} />
			<OpportunityPopup isOpen={isOpportunityOpen.isOpen} setOpen={isOpen => setIsOpportunityOpen({ isOpen })} opportunityId={isOpportunityOpen.data} />
		</TimelineContainer>
	);
}

export default Timeline;
